/**
 * CreditorMonthlyReport — «Urdhëresë pagese», lista mujore e kreditorëve.
 *
 * Faqja u soll te `ReportKit` si raportet e tjera dhe eksporti u bë dokument:
 * Excel dhe PDF me kokë kompanie, xhirollogari, total dhe vend nënshkrimi
 * (`utils/creditorOrderExport`). CSV-ja u hoq me kërkesë — një urdhëresë pagese
 * nuk dërgohet si tekst i ndarë me presje, dhe Excel-i e mbulon çdo përdorim që
 * kishte.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Paper, Typography, Chip, Tooltip,
  Table, TableHead, TableRow, TableCell, TableBody,
  TextField, MenuItem,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import apiService from '../../services/apiServices';
import PageHeader from '../common/PageHeader';
import SendReportEmailDialog from '../common/SendReportEmailDialog';
import {
  ReportPage, ReportFilters, StatTiles, ReportCard, ReportTotals,
  ExportMenu, LoadingRow, EmptyRow,
} from './ReportKit';
import { money } from '../../utils/reportSheetExport';
import {
  creditorOrderLayout, exportCreditorExcel, exportCreditorPdf, printCreditorOrder,
} from '../../utils/creditorOrderExport';

const MONTH_KEYS = [
  'report.january', 'report.february', 'report.march', 'report.april',
  'report.may_full', 'report.june', 'report.july', 'report.august',
  'report.september', 'report.october', 'report.november', 'report.december',
];

const fmtEur = (v) => `${money(v)} €`;

const rowName = (w) =>
  w.CreditorName || w.FullName || `${w.FirstName || ''} ${w.LastName || ''}`.trim() || '—';
const rowIban = (w) => w.CreditorIBAN || w.IBAN || w.BankAccount || '';
const rowAmount = (w) => Number(w.Amount ?? w.neto ?? w.NetSalary ?? 0) || 0;

export default function CreditorMonthlyReport() {
  const { t } = useTranslation();
  const now = useMemo(() => new Date(), []);

  const [month,   setMonth]   = useState(now.getMonth() + 1);
  const [year,    setYear]    = useState(now.getFullYear());
  const [items,   setItems]   = useState([]);
  const [company, setCompany] = useState({});
  const [loading, setLoading] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  const monthName = t(MONTH_KEYS[month - 1]);
  const period    = `${monthName} ${year}`;

  const loadCompany = useCallback(() => {
    apiService().get('settings')
      .then((r) => { if (r?.success && r.data?.[0]) setCompany(r.data[0]); })
      .catch(() => {});
  }, []);

  const loadData = useCallback(() => {
    setLoading(true);
    apiService()
      .query('creditorMounthReport', `month=${month}&year=${year}`)
      .then((r) => setItems(r?.success && Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [month, year]);

  useEffect(() => { loadCompany(); }, [loadCompany]);
  useEffect(() => { loadData(); }, [loadData]);

  const stats = useMemo(() => {
    const total  = items.reduce((a, w) => a + rowAmount(w), 0);
    const noIban = items.filter((w) => !rowIban(w)).length;
    return { total, noIban, avg: items.length ? total / items.length : 0 };
  }, [items]);

  // Një përshkrim i vetëm për të tria daljet — Excel, PDF, printim.
  const layout = () => creditorOrderLayout(items, {
    company,
    period,
    t,
    fileBase: `urdheresa-e-pageses-${year}-${String(month).padStart(2, '0')}`,
  });

  const dirty = month !== now.getMonth() + 1 || year !== now.getFullYear();
  const reset = () => { setMonth(now.getMonth() + 1); setYear(now.getFullYear()); };

  const HEAD = [
    t('report.nr'), t('report.creditorName'), t('report.personalNr'), 'IBAN',
    t('report.jobType'), t('report.department'), t('report.branch'), t('report.amount'),
  ];

  return (
    <ReportPage>
      <PageHeader title={t('reports.creditorMonthly')} subtitle={t('reports.creditorMonthlyDesc')} />

      <ReportFilters onRefresh={loadData} onReset={reset} dirty={dirty}>
        <TextField select size="small" label={t('report.month')} value={month}
          onChange={(e) => setMonth(Number(e.target.value))}>
          {MONTH_KEYS.map((k, i) => <MenuItem key={k} value={i + 1}>{t(k)}</MenuItem>)}
        </TextField>
        <TextField select size="small" label={t('report.year')} value={year}
          onChange={(e) => setYear(Number(e.target.value))}>
          {Array.from({ length: 5 }, (_, i) => now.getFullYear() - i).map((y) => (
            <MenuItem key={y} value={y}>{y}</MenuItem>
          ))}
        </TextField>
      </ReportFilters>

      {/* Kush paguan dhe nga cila llogari — koka e vetë dokumentit, e njëjta që
          shkon te PDF-ja. Pa të, urdhëresa është vetëm një listë emrash. */}
      {(company.CompanyName || company.BankAccount) && (
        <Paper variant="outlined" sx={{ px: 1.5, py: 1, mb: 1.25, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Box sx={{
            width: 34, height: 34, borderRadius: 1.5, flexShrink: 0,
            bgcolor: 'primary.soft', color: 'primary.onSoft',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <BusinessIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 700, lineHeight: 1.25 }}>
              {company.CompanyName || '—'}
            </Typography>
            <Typography noWrap variant="caption" color="text.secondary">
              {[
                company.NUI && `NUI: ${company.NUI}`,
                company.Address,
                company.BankName,
                company.BankAccount && `IBAN: ${company.BankAccount}`,
              ].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <Chip size="small" variant="outlined" color="primary" label={`${t('report.paymentOrderForMonth')}: ${period}`}
            sx={{ height: 22, fontSize: '0.6875rem' }} />
        </Paper>
      )}

      <StatTiles
        columns={4}
        items={[
          { label: t('report.creditors'),     value: items.length },
          { label: t('report.averageAmount'), value: fmtEur(stats.avg) },
          { label: t('report.missingIban'),   value: stats.noIban, tone: stats.noIban > 0 ? 'error' : undefined },
          { label: t('report.payableTotal'),  value: fmtEur(stats.total), accent: true },
        ]}
      />

      <ReportCard
        title={period}
        count={items.length}
        countLabel={t('report.creditors')}
        actions={
          <ExportMenu
            onExcel={() => exportCreditorExcel(layout())}
            onPdf={() => exportCreditorPdf(layout())}
            onPrint={() => printCreditorOrder(layout())}
            onEmail={() => setEmailOpen(true)}
            disabled={!items.length}
          />
        }
        footer={items.length > 0 && (
          <ReportTotals
            items={[
              { label: `${t('report.creditors')}:`,    value: items.length },
              { label: `${t('report.payableTotal')}:`, value: fmtEur(stats.total) },
              stats.noIban > 0 && { label: `${t('report.missingIban')}:`, value: stats.noIban, tone: 'error' },
            ].filter(Boolean)}
          />
        )}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {HEAD.map((h, i) => (
                <TableCell key={h} className={i === HEAD.length - 1 ? 'num' : undefined}
                  align={i === 0 ? 'center' : undefined}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <LoadingRow colSpan={HEAD.length} />
            ) : items.length === 0 ? (
              <EmptyRow colSpan={HEAD.length} message={t('report.noDataForMonth', { month: monthName, year })} />
            ) : items.map((w, i) => {
              const iban = rowIban(w);
              return (
                <TableRow key={w.Id || w.id || i} hover>
                  <TableCell align="center" sx={{ color: 'text.secondary' }}>{i + 1}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{rowName(w)}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{w.PersonalNumber || w.PersonalId || '—'}</TableCell>
                  <TableCell>
                    {iban
                      ? <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{iban}</Box>
                      : (
                        <Tooltip title={t('report.ibanMissingHint')} arrow>
                          <Chip size="small" color="error" variant="outlined" label={t('report.missingIban')}
                            sx={{ height: 20, fontSize: '0.6875rem', cursor: 'help' }} />
                        </Tooltip>
                      )}
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{w.TypeOfJob || w.Position || '—'}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{w.DepartmentName || '—'}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{w.Branch || w.BranchName || '—'}</TableCell>
                  <TableCell className="num" sx={{ fontWeight: 700 }}>{fmtEur(rowAmount(w))}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </ReportCard>

      <SendReportEmailDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        reportTitle={t('reports.creditorMonthly')}
        subtitle={period}
        columns={[
          { label: t('report.creditorName'), key: rowName, align: 'left' },
          { label: t('report.personalNr'),   key: (w) => w.PersonalNumber || w.PersonalId || '—', align: 'left' },
          { label: 'IBAN',                   key: (w) => rowIban(w) || '—', align: 'left' },
          { label: t('report.jobType'),      key: (w) => w.TypeOfJob || w.Position || '—', align: 'left' },
          { label: t('report.department'),   key: (w) => w.DepartmentName || '—', align: 'left' },
          { label: t('report.amount'),       key: rowAmount, align: 'right', format: 'currency' },
        ]}
        rows={items}
        totals={{ amount: stats.total }}
        workers={[]}
      />
    </ReportPage>
  );
}
